import { Form as FormikForm, useFormikContext } from 'formik';
import { memo, useLayoutEffect } from 'react';
import { ScrollableBody } from '../../../components/Layout/ScrollableBody';
import { KeyboardDispatchTypes, useKeyboardDispatch } from '../../../context/Keyboard/keyboard';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { SimpleKeyboard } from '../../SimpleKeyboard/SimpleKeyboard';
import css from './Form.module.scss';
import { GMButtonGroup } from '../../ButtonGroup';
import FormTranslateErrors from '../FormTranslateErrors/FormTranslateErrors';
import { FormProps } from './Form.types';
import { FormProvider } from '../../../context/Form/form';
import { useDevelopmentState } from '../../../context/Development/Development';

const Form = memo(({
    fullWidthKioskInputs,
    heading,
    topDivider,
    hideAccessibilityButton,
    disableOverflow,
    backButton,
    submitButton,
    keyboardProps,
    hideKeyboard,
    hideSpacer,
    children,
}: FormProps) => {
    const keyboardDispatch = useKeyboardDispatch();
    const { isSubmitting, submitForm } = useFormikContext();
    const { isDev, hideKeyboard: devHideKeyboard } = useDevelopmentState();

    /*
     * HACK Using a regular useEffect causes the keyboard to close when moving between two screens that contain a form.
     * This is because the cleanup function is called after the next render.
     */
    useLayoutEffect(() => {
        keyboardDispatch({ type: KeyboardDispatchTypes.KeyboardOpened });

        return () => {
            keyboardDispatch({ type: KeyboardDispatchTypes.KeyboardClosed });
        };
    }, [keyboardDispatch]);

    return (
        <FormTranslateErrors>
            <FormikForm
                data-testid={'formik-form'}
                className={css.form}
                autoComplete={isElectron() ? 'off' : 'on'}
                noValidate
            >
                <FormProvider fullWidthKioskInputs={fullWidthKioskInputs}>
                    <div className={css.formContent}>
                        <ScrollableBody
                            heading={heading}
                            topDivider={topDivider}
                            hideAccessibilityButton={hideAccessibilityButton}
                            disableOverflow={disableOverflow}
                            hideSpacer={hideSpacer}
                        >
                            {children}
                        </ScrollableBody>
                        <GMButtonGroup
                            buttons={[
                                (!isSubmitting ? (
                                    backButton
                                ) : null),
                                (!isSubmitting ? (
                                    submitButton
                                ) : null),
                            ]}
                        />
                    </div>
                </FormProvider>
                {(!hideKeyboard && !isDev) || (isDev && !devHideKeyboard) ? (
                    <SimpleKeyboard {...keyboardProps} submitForm={submitForm} />
                ) : null}
            </FormikForm>
        </FormTranslateErrors>
    );
});

export default Form;
